// PostHog Analytics Service
import posthog from "posthog-js";

class PostHogService {
  private projectApiKey: string;
  private isInitialized = false;
  private isLocalhost: boolean;

  constructor() {
    this.projectApiKey = import.meta.env.VITE_POSTHOG_API_KEY || "";
    this.isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname.includes("localhost");
  }

  /**
   * Check if analytics is enabled (user hasn't opted out)
   */
  private isAnalyticsEnabled(): boolean {
    // Disable analytics on localhost
    if (this.isLocalhost) {
      return false;
    }

    const localStorageOptOut = localStorage.getItem("analytics-opted-out");
    const posthogOptOut = this.isInitialized
      ? posthog.has_opted_out_capturing()
      : false;

    return !localStorageOptOut && !posthogOptOut;
  }

  /**
   * Initialize PostHog Analytics
   *
   * Environment Detection:
   * - Development (import.meta.env.DEV = true): Direct connection to https://eu.posthog.com
   * - Production (import.meta.env.DEV = false): Proxy connection via /analytics/api
   */
  initialize(): void {
    // Don't initialize PostHog on localhost
    if (this.isLocalhost) {
      return;
    }

    if (this.isInitialized || !this.projectApiKey) {
      return;
    }

    // Check if analytics is enabled for regular tracking
    const analyticsEnabled = this.isAnalyticsEnabled();

    // Initialize PostHog - use proxy in production, direct in development
    const isDevelopment = import.meta.env.DEV;
    const apiHost = isDevelopment ? "https://eu.posthog.com" : "/analytics/api";

    posthog.init(this.projectApiKey, {
      api_host: apiHost,
      loaded: (posthog) => {
        if (posthog) {
          // Set user properties if needed
          posthog.identify();
        }
      },
      capture_pageview: false, // We'll handle page views manually
      capture_pageleave: true, // Track when users leave the app
      disable_session_recording: true, // Disable session recording for privacy
      opt_out_capturing_by_default: !analyticsEnabled, // Respect user's analytics choice
      respect_dnt: true, // Respect Do Not Track
      debug: import.meta.env.DEV, // Enable debug mode in development
      autocapture: false, // Disable autocapture for privacy
      capture_exceptions: true, // Enable error tracking (always enabled)
    });

    this.isInitialized = true;
  }

  /**
   * Track a page view
   */
  trackPageView(url?: string): void {
    if (!this.isAnalyticsEnabled() || !this.isInitialized) {
      return;
    }

    posthog.capture("$pageview", {
      $current_url: url || window.location.href,
    });
  }

  /**
   * Track a custom event
   */
  trackEvent(
    eventName: string,
    properties?: Record<string, string | number | boolean>
  ): void {
    if (!this.isAnalyticsEnabled() || !this.isInitialized) {
      return;
    }

    posthog.capture(eventName, properties);
  }

  /**
   * Track task-related events
   */
  trackTaskCreated(topicName: string): void {
    this.trackEvent("task_created", { topic: topicName });
  }

  trackTaskCompleted(
    taskName: string,
    duration: number,
    estimatedDuration: number
  ): void {
    const accuracy = Math.round((duration / estimatedDuration) * 100);
    this.trackEvent("task_completed", {
      task_name: taskName,
      duration_minutes: Math.round(duration / (1000 * 60)),
      estimated_minutes: Math.round(estimatedDuration / (1000 * 60)),
      accuracy_percentage: accuracy,
    });
  }

  trackTaskStarted(taskName: string): void {
    this.trackEvent("task_started", { task_name: taskName });
  }

  trackTaskStopped(taskName: string, duration: number): void {
    this.trackEvent("task_stopped", {
      task_name: taskName,
      duration_minutes: Math.round(duration / (1000 * 60)),
    });
  }

  /**
   * Track app usage events
   */
  trackAnalyticsViewed(): void {
    this.trackEvent("analytics_viewed");
  }

  trackDataExported(): void {
    this.trackEvent("data_exported");
  }

  trackDataImported(): void {
    this.trackEvent("data_imported");
  }

  trackTopicCreated(topicName: string): void {
    this.trackEvent("topic_created", { topic_name: topicName });
  }

  trackTopicDeleted(topicName: string): void {
    this.trackEvent("topic_deleted", { topic_name: topicName });
  }

  trackProjectCreated(projectName: string): void {
    this.trackEvent("project_created", { project_name: projectName });
  }

  trackProjectDeleted(projectName: string): void {
    this.trackEvent("project_deleted", { project_name: projectName });
  }

  /**
   * Track privacy choices
   */
  trackPrivacyChoice(optedOut: boolean): void {
    this.trackEvent("privacy_choice", { opted_out: optedOut });
  }

  /**
   * Opt out of analytics
   */
  optOut(): void {
    if (this.isInitialized) {
      posthog.opt_out_capturing();
    }
    localStorage.setItem("analytics-opted-out", "true");
  }

  /**
   * Opt in to analytics
   */
  optIn(): void {
    if (this.isInitialized) {
      posthog.opt_in_capturing();
    }
    localStorage.removeItem("analytics-opted-out");
  }
}

export const posthogService = new PostHogService();
